/* eslint-disable no-magic-numbers */
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import Stack from '@mui/material/Stack';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import axios from 'axios';

import { useSnackbar } from 'notistack';

import Pagination from 'components/Pagination';

import Route from 'routes/Route';

import { useUser } from 'hooks/useUser';

import pageStyles, { LIST_ITEM_TEXT_PRIMARY } from './styles';

const PAGE_SIZE = 5;

interface PacientInterface {
  totalCount: number;
  pacients: {
    id: number;
    name: string;
    /** @deprecated Lista usa telefone; mantido se a API ainda enviar. */
    cpf?: string;
    cellphone?: string;
    phone?: string;
    mobile?: string;
    telephone?: string;
  }[];
}

const Pacient = (): JSX.Element => {
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { user } = useUser();
  const [rows, setRows] = useState<PacientInterface | undefined>();
  const [listLoading, setListLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const handleSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>): void => {
      setSearch(event.target.value);
    },
    [],
  );

  const getFilter = useCallback(() => {
    if (!search.trim()) {
      return undefined;
    }
    return {
      filter: {
        name: search.trim(),
      },
    };
  }, [search]);

  const fetchSearch = useCallback(() => {
    setFilterLoading(true);
    const filter = getFilter();
    axios
      .get<PacientInterface>('get-pacients', {
        params: {
          ...filter,
          doctorId: user?.id,
        },
      })
      .then(response => {
        setRows(response.data);
        setPage(1);
      })
      .catch(() => {
        enqueueSnackbar('Ocorreu um erro ao resgatar pacientes', {
          variant: 'error',
        });
      })
      .finally(() => {
        setFilterLoading(false);
      });
  }, [enqueueSnackbar, getFilter, user?.id]);

  const handlePagination = useCallback(
    (value: number) => {
      if (!rows?.pacients) return;
      if (value === page) return;

      const { pacients } = rows;
      let cursor;
      let backwards = false;
      if (value > page) {
        cursor = pacients[pacients.length - 1]?.id ?? 0;
      } else {
        cursor = pacients[0]?.id ?? 0;
        backwards = true;
      }
      const filter = getFilter();
      setPageLoading(true);
      axios
        .get<PacientInterface>('get-pacients', {
          params: {
            ...filter,
            doctorId: user?.id,
            cursor,
            backwards,
          },
        })
        .then(response => {
          setRows(response.data);
          setPage(value);
        })
        .catch(() => {
          enqueueSnackbar('Ocorreu um erro ao resgatar pacientes', {
            variant: 'error',
          });
        })
        .finally(() => {
          setPageLoading(false);
        });
    },
    [enqueueSnackbar, getFilter, page, rows, user?.id],
  );

  useEffect(() => {
    setListLoading(true);
    axios
      .get<PacientInterface>('get-pacients', {
        params: {
          doctorId: user?.id,
        },
      })
      .then(response => {
        setRows(response.data);
      })
      .catch(() => {
        enqueueSnackbar('Ocorreu um erro ao resgatar pacientes', {
          variant: 'error',
        });
      })
      .finally(() => {
        setListLoading(false);
      });
  }, [enqueueSnackbar, user?.id]);

  const goToPacient = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      navigate(Route.PACIENTMENU, {
        state: { id: event.currentTarget.name, name: event.currentTarget.id },
      });
    },
    [navigate],
  );

  const rowData = useMemo(() => {
    if (listLoading || filterLoading || pageLoading) {
      return (
        <Box sx={pageStyles.loadingBox}>
          <CircularProgress aria-label="Carregando pacientes" />
        </Box>
      );
    }
    if (!rows?.pacients?.length) {
      return (
        <Box sx={pageStyles.emptyState}>
          <InboxOutlinedIcon sx={pageStyles.emptyIcon} />
          <Typography variant="body1">Nenhum paciente encontrado</Typography>
        </Box>
      );
    }
    return rows.pacients.map(row => {
      const phone =
        row.cellphone?.trim() ||
        row.phone?.trim() ||
        row.mobile?.trim() ||
        row.telephone?.trim() ||
        '—';
      return (
        <ListItem
          key={row.id}
          component={Paper}
          variant="outlined"
          sx={pageStyles.listItemPaper}
        >
          <ListItemAvatar>
            <Avatar sx={pageStyles.listAvatar}>
              <PersonIcon />
            </Avatar>
          </ListItemAvatar>
          <ListItemText
            primary={row.name}
            secondary={`Telefone: ${phone}`}
            slotProps={{ primary: LIST_ITEM_TEXT_PRIMARY }}
          />
          <IconButton
            edge="end"
            name={`${row.id}`}
            id={row.name}
            onClick={goToPacient}
            aria-label={`Abrir menu de ${row.name}`}
          >
            <OpenInNewOutlined />
          </IconButton>
        </ListItem>
      );
    });
  }, [filterLoading, goToPacient, listLoading, pageLoading, rows]);

  const busy = listLoading || filterLoading || pageLoading;

  return (
    <Container maxWidth="md" sx={pageStyles.pageContainer}>
      <Typography
        variant="h5"
        component="h1"
        gutterBottom
        sx={pageStyles.pageTitle}
      >
        Pacientes
      </Typography>
      <Paper variant="outlined" sx={pageStyles.filtersPaper}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={pageStyles.filtersStack}
        >
          <TextField
            name="search"
            label="Buscar por nome"
            placeholder="Digite o nome…"
            value={search}
            onChange={handleSearch}
            fullWidth
            size="small"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" aria-hidden />
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button
            variant="contained"
            onClick={fetchSearch}
            disabled={busy}
            sx={pageStyles.filterButton}
          >
            {filterLoading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              'Filtrar'
            )}
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={pageStyles.listPaper} aria-busy={busy}>
        <List disablePadding>{rowData}</List>
      </Paper>

      {rows && rows.totalCount > 0 ? (
        <Box sx={pageStyles.paginationWrap}>
          <Pagination
            total={Math.ceil(rows.totalCount / PAGE_SIZE)}
            page={page}
            onChange={handlePagination}
            disabled={busy}
          />
        </Box>
      ) : null}
    </Container>
  );
};

export default Pacient;
